import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useWatch } from 'react-hook-form'
import type {
  Control,
  UseFormRegister,
  UseFormSetValue,
  FieldErrors,
  FieldArrayWithId,
} from 'react-hook-form'

import { MANAGE_FIELD_KEY, useIsFieldHidden } from '#/shared/lib/manage-fields'
import { cn } from '#/shared/lib/utils'
import { Checkbox } from '#/shared/ui/checkbox'
import { PlusIcon } from '#/shared/ui/icons'
import { Input } from '#/shared/ui/input'
import { Select } from '#/shared/ui/select'
import { resolveTypeSuggestion } from '../lib/type-suggestion'
import type { VehiclesFormData } from '../model/vehicles-schema'
import { VEHICLE_TYPE_VALUES } from '../model/vehicles-schema'
import type { VinDecodeResult } from '../model/use-vin-decode'
import { MakeCombobox } from './make-combobox'
import { ModelCombobox } from './model-combobox'
import { VinCombobox } from './vin-combobox'

interface VehiclesCardProps {
  control: Control<VehiclesFormData>
  register: UseFormRegister<VehiclesFormData>
  setValue?: UseFormSetValue<VehiclesFormData>
  errors: FieldErrors<VehiclesFormData>
  fields: FieldArrayWithId<VehiclesFormData, 'vehicles', 'id'>[]
  bare?: boolean
  hideTitle?: boolean
  onAdd: () => void
  onRemove: (index: number) => void
}

const FIELD_PROPS = {
  inputSize: 'sm' as const,
  labelSize: 'sm' as const,
  clearable: true,
}

function useTypeOptions() {
  const { t } = useTranslation()
  // The canonical type list is static (matches the VIN decoder's
  // `vehicle_type` vocabulary) — no need to fetch `/vehicles/types`.
  return VEHICLE_TYPE_VALUES.map((value) => ({
    value,
    label: t(`orderCreate.vehicles.typeOptions.${value}`),
  }))
}

function VehiclesHeader({
  count,
  hideTitle,
  onAdd,
}: {
  count: number
  hideTitle?: boolean
  onAdd: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex h-6 items-center gap-2.5 px-2.5">
      <div className="flex items-center gap-1.25">
        {!hideTitle && (
          <span className="text-xs uppercase text-gray-500">
            {t('orderCreate.vehicles.title')}
          </span>
        )}
        <button
          type="button"
          onClick={onAdd}
          className="ml-1.25 flex h-5 cursor-pointer items-center gap-1 rounded-md border border-gray-200 bg-white px-1.5 text-xs text-primary transition-colors hover:bg-gray-50"
        >
          <PlusIcon className="size-2.5" />
          {t('orderCreate.vehicles.addVehicle')}
        </button>
      </div>
      <span className="ml-auto text-xs text-gray-500">
        {t('orderCreate.vehicles.count', { count })}
      </span>
    </div>
  )
}

interface VehicleRowProps {
  index: number
  register: UseFormRegister<VehiclesFormData>
  control: Control<VehiclesFormData>
  setValue?: UseFormSetValue<VehiclesFormData>
  errors: FieldErrors<VehiclesFormData>
  canRemove: boolean
  onRemove: () => void
}

type VehicleItemErrors = NonNullable<
  NonNullable<FieldErrors<VehiclesFormData>['vehicles']>[number]
>

function YearField({
  index,
  register,
  itemErrors,
  disabled,
}: {
  index: number
  register: UseFormRegister<VehiclesFormData>
  itemErrors: VehicleItemErrors
  disabled?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="w-15 shrink-0">
      <Input
        {...FIELD_PROPS}
        {...register(`vehicles.${index}.year`)}
        clearable={false}
        label={`${t('orderCreate.vehicles.year')} *`}
        placeholder={disabled ? t('orderCreate.vehicles.lookingUp') : undefined}
        inputMode="numeric"
        maxLength={4}
        error={!!itemErrors.year}
        disabled={disabled}
      />
    </div>
  )
}

type DownstreamVehicleField = 'model' | 'type' | 'lot' | 'vin'

function clearVehicleFields(
  setValue: UseFormSetValue<VehiclesFormData>,
  index: number,
  fields: DownstreamVehicleField[],
): void {
  for (const field of fields) {
    setValue(`vehicles.${index}.${field}`, '', {
      shouldValidate: true,
      shouldDirty: true,
    })
  }
}

function MakeField({
  index,
  control,
  setValue,
  itemErrors,
  disabled,
  onTypeAutoDetected,
}: {
  index: number
  control: Control<VehiclesFormData>
  setValue?: UseFormSetValue<VehiclesFormData>
  itemErrors: VehicleItemErrors
  disabled?: boolean
  onTypeAutoDetected?: (detected: boolean) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="min-w-0 flex-1">
      <Controller
        control={control}
        name={`vehicles.${index}.make`}
        render={({ field }) => (
          <MakeCombobox
            label={`${t('orderCreate.vehicles.make')} *`}
            placeholder={
              disabled
                ? t('orderCreate.vehicles.lookingUp')
                : t('orderCreate.vehicles.placeholders.make')
            }
            value={field.value}
            onValueChange={(next) => {
              field.onChange(next)
              if (!next && setValue) {
                clearVehicleFields(setValue, index, [
                  'model',
                  'type',
                  'lot',
                  'vin',
                ])
                onTypeAutoDetected?.(false)
              }
            }}
            onSelect={() => {
              if (!setValue) return
              // Type is independent (backend can't derive it from make/model);
              // only the model/lot/vin depend on the make.
              clearVehicleFields(setValue, index, ['model', 'lot', 'vin'])
              onTypeAutoDetected?.(false)
            }}
            error={!!itemErrors.make}
            disabled={disabled}
          />
        )}
      />
    </div>
  )
}

function ModelField({
  index,
  control,
  setValue,
  itemErrors,
  disabled,
  onTypeAutoDetected,
}: {
  index: number
  control: Control<VehiclesFormData>
  setValue?: UseFormSetValue<VehiclesFormData>
  itemErrors: VehicleItemErrors
  disabled?: boolean
  onTypeAutoDetected?: (detected: boolean) => void
}) {
  const { t } = useTranslation()
  const make = useWatch({ control, name: `vehicles.${index}.make` })
  return (
    <div className="min-w-0 flex-1">
      <Controller
        control={control}
        name={`vehicles.${index}.model`}
        render={({ field }) => (
          <ModelCombobox
            label={`${t('orderCreate.vehicles.model')} *`}
            placeholder={
              disabled
                ? t('orderCreate.vehicles.lookingUp')
                : t('orderCreate.vehicles.placeholders.model')
            }
            make={make}
            value={field.value}
            onValueChange={(next) => {
              field.onChange(next)
              if (!next && setValue) {
                clearVehicleFields(setValue, index, ['lot', 'vin'])
                onTypeAutoDetected?.(false)
              }
            }}
            onSelect={(selection) => {
              if (!setValue) return
              clearVehicleFields(setValue, index, ['lot', 'vin'])
              // The picked make+model carries a best-guess body type; how
              // assertively we apply it is confidence-gated (silent fill vs.
              // fill-and-flag vs. leave the manual choice intact).
              const mode = resolveTypeSuggestion(
                selection.type,
                selection.typeConfidence,
              )
              if (mode !== 'skip' && selection.type) {
                setValue(`vehicles.${index}.type`, selection.type, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              onTypeAutoDetected?.(mode === 'suggest')
            }}
            error={!!itemErrors.model}
            disabled={disabled}
          />
        )}
      />
    </div>
  )
}

function InopField({
  index,
  control,
}: {
  index: number
  control: Control<VehiclesFormData>
}) {
  const { t } = useTranslation()
  return (
    <div className="flex w-10 shrink-0 flex-col items-center gap-0.5">
      <span className="text-xs text-gray-500">
        {t('orderCreate.vehicles.inop')}
      </span>
      <div className="flex h-8 items-center">
        <Controller
          control={control}
          name={`vehicles.${index}.inop`}
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              onCheckedChange={(c) => field.onChange(c === true)}
              className="transition-transform active:scale-90"
            />
          )}
        />
      </div>
    </div>
  )
}

function TypeField({
  index,
  control,
  itemErrors,
  disabled,
  autoDetected,
  onClearAutoDetected,
}: {
  index: number
  control: Control<VehiclesFormData>
  itemErrors: VehicleItemErrors
  disabled?: boolean
  autoDetected?: boolean
  onClearAutoDetected?: () => void
}) {
  const { t } = useTranslation()
  const typeOptions = useTypeOptions()
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <Controller
        control={control}
        name={`vehicles.${index}.type`}
        render={({ field }) => (
          <Select
            selectSize="sm"
            labelSize="sm"
            label={t('orderCreate.vehicles.type')}
            placeholder={
              disabled ? t('orderCreate.vehicles.lookingUp') : undefined
            }
            options={typeOptions}
            value={field.value}
            onValueChange={(next) => {
              field.onChange(next)
              onClearAutoDetected?.()
            }}
            error={!!itemErrors.type}
            disabled={disabled}
          />
        )}
      />
      {autoDetected && (
        <span className="text-2xs text-gray-500">
          {t('orderCreate.vehicles.typeAutoDetected')}
        </span>
      )}
    </div>
  )
}

function LotField({
  index,
  register,
  itemErrors,
}: {
  index: number
  register: UseFormRegister<VehiclesFormData>
  itemErrors: VehicleItemErrors
}) {
  const { t } = useTranslation()
  return (
    <div className="w-20 shrink-0">
      <Input
        {...FIELD_PROPS}
        {...register(`vehicles.${index}.lot`)}
        clearable={false}
        label={t('orderCreate.vehicles.lot')}
        error={!!itemErrors.lot}
      />
    </div>
  )
}

function applyDecodedToSiblings(
  index: number,
  decoded: VinDecodeResult,
  setValue: UseFormSetValue<VehiclesFormData>,
) {
  if (!decoded.make || !decoded.model) return
  const setOpts = { shouldValidate: true, shouldDirty: true } as const
  setValue(`vehicles.${index}.make`, decoded.make, setOpts)
  setValue(`vehicles.${index}.model`, decoded.model, setOpts)
  if (decoded.year != null) {
    setValue(`vehicles.${index}.year`, String(decoded.year), setOpts)
  }
  // VIN decode always returns a canonical `vehicle_type`, which resolves
  // cleanly against the `/vehicles/types` dropdown options.
  setValue(`vehicles.${index}.type`, decoded.vehicle_type, setOpts)
}

function VinField({
  index,
  control,
  setValue,
  itemErrors,
  onLoadingChange,
  onTypeAutoDetected,
}: {
  index: number
  control: Control<VehiclesFormData>
  setValue?: UseFormSetValue<VehiclesFormData>
  itemErrors: VehicleItemErrors
  onLoadingChange?: (loading: boolean) => void
  onTypeAutoDetected?: (detected: boolean) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="w-45 shrink-0">
      <Controller
        control={control}
        name={`vehicles.${index}.vin`}
        render={({ field }) => (
          <VinCombobox
            label={t('orderCreate.vehicles.vin')}
            value={field.value}
            onValueChange={field.onChange}
            onLoadingChange={onLoadingChange}
            onDecodedSelect={(decoded) => {
              if (decoded.vin) field.onChange(decoded.vin)
              if (setValue) applyDecodedToSiblings(index, decoded, setValue)
              // VIN type is authoritative — drop any model-guess flag.
              onTypeAutoDetected?.(false)
            }}
            error={!!itemErrors.vin}
          />
        )}
      />
    </div>
  )
}

function RemoveCell({
  onRemove,
  canRemove,
}: {
  onRemove: () => void
  canRemove: boolean
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'flex w-10 shrink-0 flex-col items-center gap-0.5 transition-opacity',
        !canRemove && 'pointer-events-none opacity-0',
      )}
      aria-hidden={!canRemove}
    >
      <span className="text-xs text-gray-500">
        {t('orderCreate.vehicles.rmv')}
      </span>
      <div className="flex h-8 items-center">
        <button
          type="button"
          onClick={onRemove}
          tabIndex={canRemove ? 0 : -1}
          aria-label={t('orderCreate.vehicles.removeVehicle')}
          className="flex h-4.5 cursor-pointer items-center justify-center rounded-sm border border-gray-200 bg-red-50 px-1.25 text-gray-500 transition-colors hover:bg-red-100 active:scale-90"
        >
          <span aria-hidden className="block h-px w-2.5 bg-current" />
        </button>
      </div>
    </div>
  )
}

function VehicleRow({
  index,
  register,
  control,
  setValue,
  errors,
  canRemove,
  onRemove,
}: VehicleRowProps) {
  const itemErrors: VehicleItemErrors = errors.vehicles?.[index] ?? {}
  const [vinLoading, setVinLoading] = useState(false)
  const [typeAutoDetected, setTypeAutoDetected] = useState(false)
  const showVin = !useIsFieldHidden(MANAGE_FIELD_KEY.vehicleVin)
  return (
    <div className="flex w-full animate-slide-down flex-col gap-1.75 px-2.5">
      <div className="flex w-full items-start gap-2.5">
        <YearField
          index={index}
          register={register}
          itemErrors={itemErrors}
          disabled={vinLoading}
        />
        <MakeField
          index={index}
          control={control}
          setValue={setValue}
          itemErrors={itemErrors}
          disabled={vinLoading}
          onTypeAutoDetected={setTypeAutoDetected}
        />
        <ModelField
          index={index}
          control={control}
          setValue={setValue}
          itemErrors={itemErrors}
          disabled={vinLoading}
          onTypeAutoDetected={setTypeAutoDetected}
        />
        <InopField index={index} control={control} />
      </div>
      <div className="flex w-full items-start gap-2.5">
        <TypeField
          index={index}
          control={control}
          itemErrors={itemErrors}
          disabled={vinLoading}
          autoDetected={typeAutoDetected}
          onClearAutoDetected={() => setTypeAutoDetected(false)}
        />
        <LotField index={index} register={register} itemErrors={itemErrors} />
        {showVin && (
          <VinField
            index={index}
            control={control}
            setValue={setValue}
            itemErrors={itemErrors}
            onLoadingChange={setVinLoading}
            onTypeAutoDetected={setTypeAutoDetected}
          />
        )}
        <RemoveCell onRemove={onRemove} canRemove={canRemove} />
      </div>
    </div>
  )
}

export function VehiclesCard({
  control,
  register,
  setValue,
  errors,
  fields,
  bare = false,
  hideTitle = false,
  onAdd,
  onRemove,
}: VehiclesCardProps) {
  const Container = bare ? 'div' : 'section'
  const shellClass = bare
    ? 'flex w-full flex-col gap-2'
    : 'flex w-full flex-col gap-2 rounded-md border border-gray-200 bg-white px-2.5 pt-2.5 pb-5'
  return (
    <Container className={shellClass}>
      <VehiclesHeader
        count={fields.length}
        hideTitle={hideTitle}
        onAdd={onAdd}
      />
      <div className="flex flex-col gap-5">
        {fields.map((field, index) => (
          <VehicleRow
            key={field.id}
            index={index}
            control={control}
            register={register}
            setValue={setValue}
            errors={errors}
            canRemove={fields.length > 1}
            onRemove={() => onRemove(index)}
          />
        ))}
      </div>
    </Container>
  )
}
